// Check B của doctor: "hook telemetry đã bao giờ chạy ở workspace này chưa" — CHẨN
// ĐOÁN, `fixable: false` vĩnh viễn. Lý do và ma trận luận ở
// `assess-telemetry-hook-activity.ts`.
import { promises as fs } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import type { CheckResult } from "../commands/doctor.js";
import { assessTelemetryHookActivity } from "./assess-telemetry-hook-activity.js";
import { detectOpenClaudeCodeSession } from "./detect-open-claude-code-session.js";
import { resolveAvatarWorkspaceRootFromCwd } from "./resolve-avatar-workspace-root-from-cwd.js";

const CHECK_NAME = "Hook telemetry có chạy chưa";

export const TELEMETRY_HEARTBEAT_RELATIVE = join(".claude", "state", "telemetry-heartbeat.json");

interface HeartbeatSnapshot {
  tsMs: number | null;
  optedOut: boolean;
}

/**
 * Đọc heartbeat do pack ghi (`tools/telemetry/hooks/shared/heartbeat.py`).
 * `last_invocation_ts` là epoch GIÂY (float) — đổi sang ms cho khớp mọi mốc còn lại.
 *
 * File thiếu / JSON hỏng / thiếu field ⇒ `tsMs: null` = "chưa từng chạy". Gộp 3 ca này
 * làm một là ĐÚNG ở đây: cả ba đều có nghĩa "không có bằng chứng hook từng chạy".
 */
async function readHeartbeat(workspaceRoot: string): Promise<HeartbeatSnapshot> {
  try {
    const raw = await fs.readFile(join(workspaceRoot, TELEMETRY_HEARTBEAT_RELATIVE), "utf8");
    const parsed = JSON.parse(raw) as {
      last?: { last_invocation_ts?: unknown; opted_out?: unknown };
    };
    const ts = parsed.last?.last_invocation_ts;
    if (typeof ts !== "number" || !Number.isFinite(ts)) return { tsMs: null, optedOut: false };
    return { tsMs: ts * 1000, optedOut: parsed.last?.opted_out === true };
  } catch {
    return { tsMs: null, optedOut: false };
  }
}

/**
 * Dựng check B. `homeDir`/`now` tiêm được để test hermetic (cùng khuôn
 * `detectOpenClaudeCodeSession`).
 */
export async function buildTelemetryHookActivityCheck(
  cwd: string,
  homeDir: string = homedir(),
  now: number = Date.now(),
): Promise<CheckResult> {
  const workspaceRoot = resolveAvatarWorkspaceRootFromCwd(cwd);
  if (workspaceRoot === null) {
    // Không ở trong workspace Avatar nào ⇒ không có heartbeat để đọc. Ca "workspace
    // nằm ở thư mục con" đã có check A lo, không nói chồng ở đây.
    return {
      name: CHECK_NAME,
      status: "ok",
      detail: "thư mục hiện tại không thuộc workspace Avatar nào — không có gì để đo.",
      fixable: false,
    };
  }

  const heartbeat = await readHeartbeat(workspaceRoot);
  const session = detectOpenClaudeCodeSession(workspaceRoot, homeDir, now);
  const verdict = assessTelemetryHookActivity({
    heartbeatTsMs: heartbeat.tsMs,
    optedOut: heartbeat.optedOut,
    newestSessionMtimeMs: session.newestMtimeMs,
    sessionStatus: session.status,
    nowMs: now,
  });

  return {
    name: CHECK_NAME,
    status: verdict.status,
    detail: verdict.detail,
    // ⛔ KHÔNG BAO GIỜ fixable: hook không chạy còn do tool telemetry tắt hoặc chưa mở
    // phiên nào — tự init đè lên là chữa sai bệnh.
    fixable: false,
  };
}
